'use client';

import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Factory } from 'lucide-react';
import { FilterGroup } from '../FilterGroup';
import type { StagedFilters, StagedFilterSetters } from '../useSidebarFilterState';
import type { NaceCode } from '../searchSidebarOptions';
import {
  CpvCodeField,
  IndustryPickerField,
  NaceCodeField,
  TagsField,
} from './IndustryFilterGroupParts';

export interface IndustryFilterGroupProps {
  t: TFunction<'translation', undefined>;
  industryCount: number;
  clearIndustry: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | null;
  lockOf: (control: string) => 'user' | null;
  industryOpen: boolean;
  setIndustryOpen: Dispatch<SetStateAction<boolean>>;
  /** The staged filter values and their setters (useSidebarFilterValues). */
  staged: StagedFilters;
  set: StagedFilterSetters;
  allIndustries: { key: string; label: string }[];
  toggleIndustry: (key: string) => void;
  facetCount: (facet: string, value: string) => number | undefined;
  hideNaceCodes: boolean;
  naceCodesOpen: boolean;
  setNaceCodesOpen: Dispatch<SetStateAction<boolean>>;
  setNaceSearch: Dispatch<SetStateAction<string>>;
  naceCodes: NaceCode[];
  removeSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  naceSearch: string;
  naceListRef: RefObject<HTMLDivElement | null>;
  filteredNaceCodes: NaceCode[];
  naceCodesHaveChildren: Set<string>;
  expandedNaceNodes: Set<string>;
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  toggleNaceExpanded: (code: string) => void;
  tagsOpen: boolean;
  setTagsOpen: Dispatch<SetStateAction<boolean>>;
  setTagSearch: Dispatch<SetStateAction<string>>;
  tagSearch: string;
  tagList: string[];
  cpvCodesOpen: boolean;
  setCpvCodesOpen: Dispatch<SetStateAction<boolean>>;
  setCpvSearch: Dispatch<SetStateAction<string>>;
  cpvCodesList: { code: string; description: string }[];
  cpvSearch: string;
  cpvListRef: RefObject<HTMLDivElement | null>;
}

export function IndustryFilterGroup(props: IndustryFilterGroupProps) {
  const {
    t,
    industryCount,
    clearIndustry,
    openFilterGroup,
    handleGroupOpenChange,
    groupLock,
    hideNaceCodes,
  } = props;
  return (
    <FilterGroup
      id="industry"
      icon={Factory}
      label={t('search.sidebar.groups.industry', {
        defaultValue: 'Industry & activity',
      })}
      activeCount={industryCount}
      onClear={clearIndustry}
      open={openFilterGroup === 'industry'}
      onOpenChange={handleGroupOpenChange('industry')}
      lock={groupLock('industry')}
    >
      <IndustryPickerField {...props} />
      {!hideNaceCodes && <NaceCodeField {...props} />}
      <TagsField {...props} />
      <CpvCodeField {...props} />
      {/* A new industry/activity filter: a field component in
          IndustryFilterGroupParts.tsx reading staged.<key> / set.<key>
          (pickers build on ComboboxTrigger/FilterChip from
          FilterFieldParts.tsx), rendered here in sidebar order. */}
    </FilterGroup>
  );
}
